import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sun, FileCheck, Scale, Zap, TrendingUp } from 'lucide-react';
import { WhySolutionsSection, WhyConsultationSection, WhySwitchNowSection } from '../components/home/WhySections';
import AnimatedCounter from '../components/AnimatedCounter';
import TestimonialCarousel from '../components/TestimonialCarousel';
import LogoCarousel from '../components/LogoCarousel';
import { heroContent as mockHero, services as mockServicePillars, siteSettings as mockSettings, projects as mockProjects, testimonials as mockTestimonials, partners as mockPartners, customers as mockCustomers, regulatoryServices as mockRegServices, filterRegulatoryServices } from '../data/mock';
import { projectsAPI, partnersCustomersAPI, testimonialsAPI, contentAPI } from '../services/api';

const Home = () => {
  const [projects, setProjects] = useState(mockProjects);
  const [partners, setPartners] = useState(mockPartners);
  const [customers, setCustomers] = useState(mockCustomers);
  const [testimonials, setTestimonials] = useState(mockTestimonials);
  const [heroContent, setHeroContent] = useState(mockHero);
  const [services, setServices] = useState(mockServicePillars);
  const [siteSettings, setSiteSettings] = useState(mockSettings);
  const [regulatoryServices, setRegulatoryServices] = useState(mockRegServices);
  const [homeSections, setHomeSections] = useState(null);

  const loadDynamicData = useCallback(async () => {
    try {
      const [projRes, partRes, custRes, testRes, heroRes, svcRes, settRes, regRes, homeSecRes] = await Promise.allSettled([
        projectsAPI.getAll(),
        partnersCustomersAPI.getAll('partner'),
        partnersCustomersAPI.getAll('customer'),
        testimonialsAPI.getAll(),
        contentAPI.get('hero'),
        contentAPI.get('services_pillars'),
        contentAPI.get('site_settings'),
        contentAPI.get('regulatory_services'),
        contentAPI.get('homepage_sections')
      ]);
      if (projRes.status === 'fulfilled' && projRes.value.data.length > 0) {
        setProjects(projRes.value.data.map(p => ({ ...p, id: p._id, type: p.project_type })));
      }
      if (partRes.status === 'fulfilled' && partRes.value.data.length > 0) setPartners(partRes.value.data);
      if (custRes.status === 'fulfilled' && custRes.value.data.length > 0) setCustomers(custRes.value.data);
      if (testRes.status === 'fulfilled' && testRes.value.data.length > 0) setTestimonials(testRes.value.data);
      if (heroRes.status === 'fulfilled' && heroRes.value.data && Object.keys(heroRes.value.data).length) setHeroContent(heroRes.value.data);
      if (svcRes.status === 'fulfilled' && svcRes.value.data?.pillars) setServices(svcRes.value.data);
      if (settRes.status === 'fulfilled' && settRes.value.data && Object.keys(settRes.value.data).length) {
        setSiteSettings({
          ...settRes.value.data,
          offices: mockSettings.offices  // ALWAYS use mock offices, ignore database
        });
        if (settRes.value.data.browserTitle) document.title = settRes.value.data.browserTitle;
      }
      if (regRes.status === 'fulfilled' && Array.isArray(regRes.value.data) && regRes.value.data.length) setRegulatoryServices(filterRegulatoryServices(regRes.value.data));
      if (homeSecRes.status === 'fulfilled' && homeSecRes.value.data && Object.keys(homeSecRes.value.data).length) setHomeSections(homeSecRes.value.data);
    } catch {
      // fallback to mock data already set
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadDynamicData();
  }, [loadDynamicData]);

  const iconMap = {
    Sun: Sun,
    FileCheck: FileCheck,
    Scale: Scale
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] md:h-[700px] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={heroContent.heroImage}
            alt="Solar Farm"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in">
              {heroContent.headline}
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed">
              {heroContent.subheadline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl gap-2"
              >
                {heroContent.ctaPrimary}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-md text-white font-semibold rounded-lg border-2 border-white/30 hover:bg-white/20 transition-all duration-200"
              >
                {heroContent.ctaSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Counters Section */}
      <section className="bg-gradient-to-br from-emerald-600 to-emerald-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-center text-white">
            <div className="transform hover:scale-105 transition-transform duration-300">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Zap className="w-10 h-10" />
                <div className="text-5xl md:text-6xl font-bold">
                  <AnimatedCounter end={siteSettings.counters.projectsMW} suffix="+" />
                </div>
              </div>
              <p className="text-xl font-medium">MW Projects Delivered</p>
              <p className="text-emerald-100 text-sm mt-1">Across India</p>
            </div>
            <div className="transform hover:scale-105 transition-transform duration-300">
              <div className="flex items-center justify-center gap-3 mb-2">
                <TrendingUp className="w-10 h-10" />
                <div className="text-5xl md:text-6xl font-bold">
                  <AnimatedCounter end={siteSettings.counters.ppasDelivered} suffix="+" />
                </div>
              </div>
              <p className="text-xl font-medium">PPAs Delivered</p>
              <p className="text-emerald-100 text-sm mt-1">Successfully Executed</p>
            </div>
          </div>
        </div>
      </section>

      <WhySolutionsSection section={homeSections?.whySolutions} />

      {/* Services Pillars */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Core Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive renewable energy solutions from consulting to regulatory compliance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.pillars.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Link
                  key={service.id}
                  to={service.link}
                  className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors duration-200">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{service.summary}</p>
                  <div className="flex items-center text-emerald-600 font-medium group-hover:gap-2 transition-all duration-200">
                    Learn more
                    <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <WhyConsultationSection section={homeSections?.whyConsultation} />

      {/* Featured Regulatory Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Regulatory Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Expert support for all regulatory approvals and compliance requirements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regulatoryServices.slice(0, 6).map((service) => (
              <Link
                key={service.id}
                to={`/regulatory/${service.slug}`}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{service.description}</p>
                <div className="flex items-center text-emerald-600 font-medium text-sm">
                  View details
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/regulatory-consultancy"
              className="inline-flex items-center px-8 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              View All Regulatory Services
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      <WhySwitchNowSection section={homeSections?.whySwitchNow} />

      {/* Featured Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Featured Projects</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Delivering successful renewable energy projects across India
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.slice(0, 6).map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.slug}`}
                className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.thumbnail}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {project.mw} MW
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors duration-200">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">{project.location}</p>
                  <p className="text-gray-700 text-sm line-clamp-2">{project.summary}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/projects"
              className="inline-flex items-center px-8 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              View All Projects
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Partners</h2>
            <p className="text-lg text-gray-600">Collaborating with leading organizations in renewable energy</p>
          </div>
          <LogoCarousel logos={partners} direction="left" />
        </div>
      </section>

      {/* Customers Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Customers</h2>
            <p className="text-lg text-gray-600">Serving developers, EPC companies, and industrial clients nationwide</p>
          </div>
          <LogoCarousel logos={customers} direction="right" />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-lg text-gray-600">Success stories from our satisfied clients</p>
          </div>
          <TestimonialCarousel testimonials={testimonials} direction="left" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Power Your Renewable Journey?
          </h2>
          <p className="text-xl text-emerald-50 mb-8 leading-relaxed">
            Get in touch with our experts for tailored solutions and regulatory support
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-emerald-600 font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200 shadow-lg"
            >
              Get Tailored Solutions
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/newsletter"
              className="inline-flex items-center justify-center px-8 py-4 bg-emerald-800 text-white font-semibold rounded-lg hover:bg-emerald-900 transition-colors duration-200"
            >
              Subscribe to Newsletter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
